import { useCallback } from 'react'
import { Skeleton, styled, Typography } from '@mui/material'
import { MessageSender } from '@weblite/ts-action/message'
import { checkUserAccessibilityViaForwardWithUserId } from '$logic/user-policy'
import { openUserProfile, openWappProfile } from '$logic/router'
import { openRoom } from '$logic/opened-room'
import { t } from '$i18n'
import { showCanNotAccessViaForwardedMessageSnackbar } from '$comp/snackbars'
import { useSenderName } from '$logic/message/react-hook'

const RoleTypography = styled(Typography)`
  /* @noflip */
  margin-right: 6px;
`

function SenderName({ sender }: { sender: MessageSender }) {
  const { name, loading } = useSenderName(sender, true)
  const isSenderChannel = sender.type === 'CHANNEL'
  const role = sender.type === 'WAPP' ? t('message.tool') : null

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      {loading ? (
        <Skeleton variant="text" width="40px" height="20px" />
      ) : (
        <Typography noWrap color="secondary700" variant="subtitle4">
          {name}
        </Typography>
      )}

      {role && (
        <RoleTypography variant="caption1" color="gray">
          {role}
        </RoleTypography>
      )}
    </div>
  )
}

const ForwardedFromPrefixTypography = styled(Typography)`
  /* @noflip */
  margin-left: 5px;
  flex-shrink: 0;
`

function ForwardedFromName({
  forwardedFrom,
}: {
  forwardedFrom: MessageSender
}) {
  const { name, loading } = useSenderName(forwardedFrom, true)

  const forwardedFromChannel = forwardedFrom.type === 'CHANNEL'

  const onUserClick = async () => {
    const accessible = await checkUserAccessibilityViaForwardWithUserId(
      forwardedFrom.id,
    )
    if (accessible) await openUserProfile(forwardedFrom.id)
    else showCanNotAccessViaForwardedMessageSnackbar()
  }

  const onForwardedFromClick = useCallback(async () => {
    if (forwardedFrom.type === 'USER') await onUserClick()
    if (forwardedFrom.type === 'CHANNEL') await openRoom(forwardedFrom.id)
    if (forwardedFrom.type === 'WAPP') await openWappProfile(forwardedFrom.id)
  }, [forwardedFrom])

  return (
    <div style={{ display: 'flex', overflow: 'hidden' }}>
      <ForwardedFromPrefixTypography variant="subtitle4" color="gray">
        {t('message.forwardedFrom')}
      </ForwardedFromPrefixTypography>

      {loading ? (
        <Skeleton
          variant="text"
          width="40px"
          height="20px"
          sx={{ display: 'inline-block', lineHeight: '20px' }}
        />
      ) : name ? (
        <Typography
          noWrap
          color="secondary"
          variant="subtitle4"
          sx={{ cursor: 'pointer' }}
          onClick={onForwardedFromClick}
        >
          {name}
        </Typography>
      ) : forwardedFromChannel ? (
        <Typography
          noWrap
          color="neutral700"
          variant="subtitle4"
          onClick={onForwardedFromClick}
        >
          {t('common.privateChannel')}
        </Typography>
      ) : (
        ''
      )}
    </div>
  )
}

export function SenderInfo({
  sender,
  forwardedFrom,
}: {
  sender?: MessageSender
  forwardedFrom?: MessageSender
}) {
  return (
    <div
      style={{
        width: '100%',
        overflow: 'hidden',
        boxSizing: 'border-box',
        /* @noflip */
        padding: '8px 10px 0',
      }}
    >
      {sender && <SenderName sender={sender} />}
      {forwardedFrom && <ForwardedFromName forwardedFrom={forwardedFrom} />}
    </div>
  )
}

SenderInfo.defaultProps = {
  sender: null,
  forwardedFrom: null,
}
